import { VALHALLA_URL } from './providers'
import { alongLine, ascentDescent, cumulativeMeters, lineDistanceMeters, type LngLat, type LngLatEle } from './geo'

export interface ElevationProfile {
  /** [distance from start (m), elevation (m)] samples */
  samples: [number, number][]
  ascentM: number
  descentM: number
  minEleM: number
  maxEleM: number
}

const MAX_SAMPLES = 300

/**
 * Sample elevations along a line via the Valhalla /height endpoint and
 * write interpolated elevations back onto the route coordinates (mutates copies).
 */
export async function elevationForLine(
  coords: readonly LngLatEle[],
  signal?: AbortSignal,
): Promise<{ profile: ElevationProfile; coords: LngLatEle[] } | null> {
  if (coords.length < 2) return null
  const totalM = lineDistanceMeters(coords)
  if (totalM <= 0) return null
  const n = Math.min(MAX_SAMPLES, Math.max(30, Math.round(totalM / 50)))
  const positions: LngLat[] = []
  const dists: number[] = []
  for (let i = 0; i <= n; i++) {
    const d = (totalM * i) / n
    positions.push(alongLine(coords, d))
    dists.push(d)
  }

  const res = await fetch(`${VALHALLA_URL}/height`, {
    method: 'POST',
    body: JSON.stringify({ shape: positions.map((p) => ({ lon: p[0], lat: p[1] })) }),
    signal,
  })
  if (!res.ok) return null
  const data = (await res.json()) as { height?: (number | null)[] }
  if (!data.height || data.height.length !== positions.length) return null

  const heights = data.height.map((h) => h ?? 0)
  // light smoothing: 3-sample moving average knocks out DEM noise
  const smooth = heights.map((_, i) => {
    const a = heights[Math.max(0, i - 1)]
    const b = heights[i]
    const c = heights[Math.min(heights.length - 1, i + 1)]
    return (a + b + c) / 3
  })

  const samples: [number, number][] = smooth.map((h, i) => [dists[i], h])
  const { ascent, descent } = ascentDescent(smooth, 3)
  const profile: ElevationProfile = {
    samples,
    ascentM: ascent,
    descentM: descent,
    minEleM: Math.round(Math.min(...smooth)),
    maxEleM: Math.round(Math.max(...smooth)),
  }

  // interpolate sampled elevations back onto the full geometry
  const cum = cumulativeMeters(coords)
  const stepM = totalM / n
  const out: LngLatEle[] = coords.map((c, i) => {
    const idx = Math.min(n, Math.max(0, cum[i] / stepM))
    const lo = Math.floor(idx)
    const hi = Math.min(n, lo + 1)
    const t = idx - lo
    return [c[0], c[1], Math.round((smooth[lo] * (1 - t) + smooth[hi] * t) * 10) / 10]
  })
  return { profile, coords: out }
}

/** Build a profile from coordinates that already carry elevation (recorded/imported tours). */
export function profileFromCoords(coords: readonly LngLatEle[]): ElevationProfile | null {
  if (coords.length < 2) return null
  const cum = cumulativeMeters(coords)
  const eles = coords.map((c) => c[2] ?? 0)
  if (!eles.some((e) => e !== 0)) return null
  const { ascent, descent } = ascentDescent(eles, 3)
  return {
    samples: coords.map((c, i) => [cum[i], c[2] ?? 0]),
    ascentM: ascent,
    descentM: descent,
    minEleM: Math.round(Math.min(...eles)),
    maxEleM: Math.round(Math.max(...eles)),
  }
}
